// Wire <-> form shape conversions for create_record (address, funnel/stage, frequencyInterval,
// groups, files, custom fields) — kept separate so createRecordDraft.ts can stay about the
// draft/apply contract.

import { ChatCustomFieldValueRaw } from "@mapmycustomers/shared/types/chat/ChatContent";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import { CustomFieldValueType } from "@mapmycustomers/shared/types/customField/CustomFieldValueType";
import { Group } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import { isRecord } from "@mapmycustomers/shared/util/chat/chatWireGuards";
import { OptionalFields } from "@mapmycustomers/shared/util/ts";

import { getAddress } from "@app/component/createEditEntity/util/getAddress";
import buildCustomFieldValue from "@app/util/customField/buildCustomFieldValue";
import loggingService from "@app/util/logging";

const idOf = (value: unknown): number | undefined =>
  isRecord(value) && typeof value.id === "number" ? value.id : undefined;

// The owner travels as `user: { id }` — the platform reads `dto.user.id` and ignores a flat
// `userId` entirely — while the form's owner control is keyed by a bare user id.
const userIdOf = (value: unknown): number | undefined =>
  typeof value === "number" ? value : idOf(value);

const isGeoPoint = (value: unknown): value is { coordinates: [number, number] } =>
  isRecord(value) &&
  Array.isArray(value.coordinates) &&
  typeof value.coordinates[0] === "number" &&
  typeof value.coordinates[1] === "number";

// An id-bearing list reaches these conversions spelled either way — objects off the wire, bare ids
// back out of a control or a persisted draft — so both collapse to ids here. Anything without one
// is dropped rather than carried as a hole the platform would reject.
const idsOf = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.flatMap((entry) => {
        const id = typeof entry === "number" ? entry : idOf(entry);
        return id === undefined ? [] : [id];
      })
    : [];

// A group travels as an object carrying the name the card renders, while the form's control is keyed
// by a Set of bare ids (GroupsSelection's selectedGroupIds). The one value key whose two shapes
// share nothing, so passing it through leaves the picker unable to read its own value.
const groupIdsFormValue = (groups: unknown): Set<Group["id"]> | undefined => {
  const ids = idsOf(groups);
  // undefined rather than an empty Set: antd's `required` rule counts any object as a value, so an
  // empty Set would silently satisfy a layout marking Groups required.
  return ids.length ? new Set(ids) : undefined;
};

// The reverse, back to the { id, name } objects the wire carries and the card's own tags read off
// the draft. The groups store is the only name there is, so an id it no longer knows travels bare
// rather than failing the save — the platform assigns by id either way.
const groupsWireValue = (
  groupIds: unknown,
  definitions: Group[]
): { id: Group["id"]; name?: Group["name"] }[] | undefined => {
  if (!(groupIds instanceof Set) && !Array.isArray(groupIds)) {
    // Absent, not cleared. Omitting the key is what keeps an untouched Save equal to the proposal,
    // so no draft is stored and finalValues stays sparse; an empty Set still emits [].
    return undefined;
  }
  return idsOf(Array.from(groupIds as Iterable<unknown>)).map((id) => {
    const definition = definitions.find((candidate) => candidate.id === id);
    return definition ? { id: definition.id, name: definition.name } : { id };
  });
};

// The form's file control emits whole RawFile objects (FileUploader's onChange), but a create takes
// ids alone — CreatePersonPayload.files is MmcFile["id"][], which is what the shipped Create modals
// send. Sending the objects is why an attachment never landed on the created record.
const fileIdsWireValue = (files: unknown): RawFile["id"][] | undefined => {
  const ids = idsOf(files);
  // Omitted rather than sent empty, exactly as CreatePersonModal's own saga does. The control fires
  // an onChange([]) of its own right after mount, so an empty list means "nothing attached" — and
  // a create has nothing to clear, so there is no case where [] says something absence does not.
  return ids.length ? ids : undefined;
};

// Wire customFields entries already carry customFieldId (unlike log_activity/record_update, which
// address by esKey alone), so the id-keyed form record is built straight from the array.
const customFieldsFormValue = (
  customFields: unknown,
  definitions: CustomField[]
): Record<string, OptionalFields<CustomFieldValue, "id">> => {
  const result: Record<string, OptionalFields<CustomFieldValue, "id">> = {};
  if (!Array.isArray(customFields)) {
    return result;
  }
  for (const entry of customFields) {
    if (!isRecord(entry)) {
      continue;
    }
    const definition = definitions.find((candidate) => candidate.id === entry.customFieldId);
    if (!definition) {
      // No control to build for a deleted definition; the entry is dropped, not shown unlabeled.
      loggingService.warning("Dropping a proposed custom field the org no longer defines", {
        customFieldId: entry.customFieldId,
      });
      continue;
    }
    const value = entry.value as CustomFieldValueType;
    result[String(definition.id)] = buildCustomFieldValue(definition, value);
  }
  return result;
};

// The reverse of customFieldsFormValue: each form entry already carries customField.id, re-
// validated here since a field deleted mid-session has no id left to write it under.
const toCreateRecordCustomFields = (
  customFields: unknown,
  definitions: CustomField[]
): ChatCustomFieldValueRaw[] => {
  if (!isRecord(customFields)) {
    return [];
  }
  return Object.values(customFields).flatMap((entry) => {
    if (!isRecord(entry) || !isRecord(entry.customField)) {
      return [];
    }
    const customFieldId = entry.customField.id;
    const definition = definitions.find((candidate) => candidate.id === customFieldId);
    if (!definition) {
      loggingService.warning("Dropping a drafted custom field the org no longer defines", {
        customFieldId,
      });
      return [];
    }
    return [
      {
        customFieldId: definition.id,
        esKey: definition.esKey,
        value: entry.value as Exclude<CustomFieldValueType, undefined>,
      },
    ];
  });
};

// Seven flat wire keys + geoPoint become addressDetails + latitude/longitude; funnel/stage become
// funnelId/stageId; frequencyInterval is offered under cadenceInterval, the form's write column.
export const wireToFormValues = (
  wire: Record<string, unknown>,
  customFieldDefinitions: CustomField[]
): Record<string, unknown> => {
  const {
    address,
    cadenceInterval,
    city,
    country,
    countryCode,
    customFields,
    frequencyInterval,
    funnel,
    geoPoint,
    groups,
    postalCode,
    region,
    regionCode,
    stage,
    user,
    ...rest
  } = wire;

  return {
    ...rest,
    addressDetails: { address, city, country, countryCode, postalCode, region, regionCode },
    cadenceInterval: cadenceInterval ?? frequencyInterval,
    customFields: customFieldsFormValue(customFields, customFieldDefinitions),
    funnelId: idOf(funnel),
    groups: groupIdsFormValue(groups),
    latitude: isGeoPoint(geoPoint) ? String(geoPoint.coordinates[1]) : "",
    longitude: isGeoPoint(geoPoint) ? String(geoPoint.coordinates[0]) : "",
    stageId: idOf(stage),
    user: userIdOf(user),
  };
};

// The reverse of wireToFormValues, via getAddress (the util CreateCompanyModal uses) for the
// address. Per-entity stripping is the caller's job, not this shape conversion's.
export const formToWireValues = (
  values: Record<string, unknown>,
  customFieldDefinitions: CustomField[],
  // The org's groups for the entity being created — the only place the name of a chosen group can
  // be read back from, since the form holds ids alone.
  groupDefinitions: Group[]
): Record<string, unknown> => {
  const {
    addressDetails,
    customFields,
    files,
    funnelId,
    groups,
    latitude,
    longitude,
    stageId,
    user,
    ...rest
  } = values;
  const wireValues: Record<string, unknown> = { ...rest, ...getAddress(values) };

  if (typeof funnelId === "number") {
    wireValues.funnel = { id: funnelId };
  }
  if (typeof stageId === "number") {
    wireValues.stage = { id: stageId };
  }
  // Omitted rather than sent empty when the rep clears it: the platform then owns the record to the
  // requesting user, which beats writing a null owner it would reject anyway.
  const ownerId = userIdOf(user);
  if (ownerId !== undefined) {
    wireValues.user = { id: ownerId };
  }

  const wireGroups = groupsWireValue(groups, groupDefinitions);
  if (wireGroups) {
    wireValues.groups = wireGroups;
  }

  const fileIds = fileIdsWireValue(files);
  if (fileIds) {
    wireValues.files = fileIds;
  }

  const rawCustomFields = toCreateRecordCustomFields(customFields, customFieldDefinitions);
  if (rawCustomFields.length) {
    wireValues.customFields = rawCustomFields;
  }
  return wireValues;
};
